/* eslint-disable react-hooks/exhaustive-deps */
import { Layout } from 'antd';
import styles from './index.module.scss';
import { Navigate, Route, Routes } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import userStore from '../../stores/userStore';
import { observer } from 'mobx-react';
import { HelmetProvider } from 'react-helmet-async';
import Sidebar from './components/Sidebar';
import Header from './components/Header';

const ApprovalView = lazy(() => import('../Approval/ApprovalView'));
const ProjectsView = lazy(() => import('../Projects/ProjectsView'));
const SettingsView = lazy(() => import('../Settings/SettingsView'));
const ReportsView = lazy(() => import('../Reports/ReportsView'));
const PersonalReportView = lazy(() => import('../Project/views/PersonalReport/PersonalReportView'));
const ProjectView = lazy(() => import('../Project/ProjectView'));
const FaqView = lazy(() => import('../Faq/FaqView'));
const ProfileView = lazy(() => import('../Profile/ProfileView'));
const AdminView = lazy(() => import('../Admin/AdminView'));

const SIDEBAR_WIDTH_OPEN = 260;
const SIDEBAR_WIDTH_COLLAPSED = 140;

// antd Sider defaults, mirrored here so fixed-position chrome can align to it.
const SIDER_OPEN = 200;
const SIDER_COLLAPSED = 80;

const DashboardView = observer(() => {
  const [isCollapsed, setCollapsed] = useState(localStorage.getItem('menuState') !== 'open');

  const toggleSidebar = () => {
    setCollapsed((value) => {
      localStorage.setItem('menuState', value ? 'open' : 'hidden');
      return !value;
    });
  };

  // Fixed-position chrome (the Scaffold's compact header, the Marketing
  // banner) is laid out against the sidebar, so its width is published as
  // custom properties rather than each component reading localStorage.
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--sidebar-left', `${isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_OPEN}px`);
    root.setProperty('--sidebar-width', `${isCollapsed ? SIDER_COLLAPSED : SIDER_OPEN}px`);
  }, [isCollapsed]);

  useEffect(() => {
    userStore.findUser();
  }, []);

  if (!userStore.user) {
    return <div />;
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Layout style={{ position: 'relative' }}>
          <Sidebar isCollapsed={isCollapsed} />
          <Layout style={{ backgroundColor: 'transparent' }}>
            <Header isCollapsed={isCollapsed} onToggleSidebar={toggleSidebar} />
            <div className={styles.content}>
              {userStore.user?.isTermsApproval ? (
                <Loading>
                  <ApprovalView />
                </Loading>
              ) : (
                <Routes>
                  <Route path="/">
                    <Route
                      path="projects"
                      element={
                        <Loading>
                          <ProjectsView />
                        </Loading>
                      }
                    />
                    <Route
                      path="directory"
                      element={
                        <Loading>
                          <SettingsView />
                        </Loading>
                      }
                    />
                    <Route
                      path="reports"
                      element={
                        <Loading>
                          <ReportsView />
                        </Loading>
                      }
                    />
                    <Route
                      path="project/:id"
                      element={
                        <Loading>
                          <ProjectView />
                        </Loading>
                      }
                    />
                    <Route
                      path="project/:id/:subjectId"
                      element={
                        <Loading>
                          <PersonalReportView />
                        </Loading>
                      }
                    />
                    <Route
                      path="faq"
                      element={
                        <Loading>
                          <FaqView />
                        </Loading>
                      }
                    />
                    <Route
                      path="profile"
                      element={
                        <Loading>
                          <ProfileView />
                        </Loading>
                      }
                    />
                    <Route
                      path="admin"
                      element={
                        <Loading>
                          <AdminView />
                        </Loading>
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <Loading>
                          <Navigate to="/projects" />
                        </Loading>
                      }
                    />
                  </Route>
                </Routes>
              )}
            </div>
          </Layout>
        </Layout>
      </BrowserRouter>
    </HelmetProvider>
  );
});

export default DashboardView;

function Loading({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div />}>{children}</Suspense>;
}
